import { ArrowLeft, ArrowRight, BringToFront, Cloud, FlipHorizontal2, Mountain, SendToBack, Trash2, Trees, type LucideIcon } from 'lucide-react'

import { SCENERY_BANDS, type SceneryBand, type SceneryCandidate, type SceneryItem } from '@/api/stages'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { cn } from '@/components/ui/utils'
import { EnabledSwitch } from '@/features/actions/parts'

import { byZ, clampX, clampY, driftParts, DRIFT_SPEEDS, itemThumb, makeDrift, MAX_HEIGHT_PCT, MIN_HEIGHT_PCT, removeItem, resizeItem, setBand, shiftZ, updateItem } from './scenery'
import { BAND_LABEL, BAND_SHORT, clipLabel } from './words'

/**
 * The selected thing, in plain controls: where it lives (sky / middle / ground), how big it is,
 * which way it faces, how it moves (its ambient clip), whether it drifts across, in front of or
 * behind the others, and Remove. The exact position sits folded away.
 */

const SELECT = 'h-10 w-full rounded-xl border border-line bg-paper px-2 text-sm text-ink focus-visible:focus-ring'
const BAND_ICON: Record<SceneryBand, LucideIcon> = { sky: Cloud, mid: Trees, ground: Mountain }

export interface SceneryInspectorProps {
  item: SceneryItem
  items: readonly SceneryItem[]
  candidate?: SceneryCandidate
  candidates?: ReadonlyMap<string, SceneryCandidate>
  /** A whole step (one undo). */
  onChange(items: SceneryItem[]): void
  onGestureStart(): void
  onLive(items: SceneryItem[]): void
  onGestureEnd(): void
  onRemoved(): void
}

export function SceneryInspector({ item, items, candidate, candidates, onChange, onGestureStart, onLive, onGestureEnd, onRemoved }: SceneryInspectorProps) {
  const order = byZ(items)
  const pos = order.findIndex((i) => i.id === item.id)
  const clips = [...new Set([...(candidate?.clips ?? []), ...(candidate?.ambient ? [candidate.ambient] : []), ...(item.clip ? [item.clip] : [])])]
  const drift = driftParts(item.drift)
  const thumb = itemThumb(item, candidates)
  const set = (patch: Partial<Omit<SceneryItem, 'id'>>) => onChange(updateItem(items, item.id, patch))

  return (
    <section className="flex flex-col gap-4" aria-label={`${item.name}: settings`} data-testid="scenery-inspector">
      <div className="flex items-center gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-paper-2">
          {thumb ? <img src={thumb} alt="" className="size-full object-contain p-1" style={item.flip ? { transform: 'scaleX(-1)' } : undefined} /> : null}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-ink" title={item.name}>
            {item.name}
          </h3>
          <p className="text-xs text-ink-3">{BAND_LABEL[item.band]}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-danger"
          onClick={() => {
            onChange(removeItem(items, item.id))
            onRemoved()
          }}
          data-testid="remove-item"
        >
          <Trash2 aria-hidden />
          Remove
        </Button>
      </div>

      <Group label="Where it lives">
        <div className="grid grid-cols-3 gap-1 rounded-xl bg-paper-2 p-1" role="radiogroup" aria-label="Where it lives">
          {SCENERY_BANDS.map((b) => {
            const Icon = BAND_ICON[b]
            const on = item.band === b
            return (
              <button
                key={b}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => !on && onChange(setBand(items, item.id, b))}
                className={cn('inline-flex h-9 items-center justify-center gap-1.5 rounded-lg text-sm font-medium text-ink-2 transition-colors focus-visible:focus-ring', on ? 'bg-paper text-ink shadow-card' : 'hover:text-ink')}
                data-testid={`band-${b}`}
              >
                <Icon className="size-4" aria-hidden />
                {BAND_SHORT[b]}
              </button>
            )
          })}
        </div>
      </Group>

      <Group label="Size" aside={`${Math.round(item.height_pct)}% of the picture`}>
        <Slider
          value={[item.height_pct]}
          min={MIN_HEIGHT_PCT}
          max={MAX_HEIGHT_PCT}
          step={1}
          aria-label="Size"
          onPointerDown={onGestureStart}
          onValueChange={([h]) => {
            if (h === undefined) return
            onLive(resizeItem(items, item.id, h))
          }}
          onValueCommit={() => onGestureEnd()}
          onKeyDown={onGestureStart}
          data-testid="size-slider"
        />
      </Group>

      <div className="grid grid-cols-2 gap-2">
        <Button variant={item.flip ? 'primary' : 'secondary'} size="sm" aria-pressed={item.flip} onClick={() => set({ flip: !item.flip })} data-testid="flip-item">
          <FlipHorizontal2 aria-hidden />
          {item.flip ? 'Flipped' : 'Flip it'}
        </Button>
        <div className="flex gap-1">
          <Button variant="secondary" size="sm" className="flex-1 px-2" onClick={() => onChange(shiftZ(items, item.id, -1))} disabled={pos <= 0} aria-label="Send back" title="Send back (behind the others)" data-testid="send-back">
            <SendToBack aria-hidden />
          </Button>
          <Button variant="secondary" size="sm" className="flex-1 px-2" onClick={() => onChange(shiftZ(items, item.id, 1))} disabled={pos < 0 || pos >= order.length - 1} aria-label="Bring forward" title="Bring forward (in front of the others)" data-testid="bring-forward">
            <BringToFront aria-hidden />
          </Button>
        </div>
      </div>

      <Group label="How it moves">
        <select className={SELECT} value={item.clip ?? ''} onChange={(e) => set({ clip: e.target.value || null })} aria-label="How it moves" data-testid="clip-select">
          <option value="">{clipLabel(null)}</option>
          {clips.map((c) => (
            <option key={c} value={c}>
              {clipLabel(c)}
            </option>
          ))}
        </select>
      </Group>

      <Group label="Drifts across the picture">
        <div className="flex flex-wrap items-center gap-2">
          <EnabledSwitch checked={drift.on} onChange={(on) => set({ drift: on ? makeDrift(drift.dir, drift.speed) : null })} label="Drifts across the picture" />
          {drift.on ? (
            <>
              <div className="flex gap-1" role="radiogroup" aria-label="Which way">
                {([-1, 1] as const).map((d) => (
                  <Button
                    key={d}
                    variant={drift.dir === d ? 'primary' : 'secondary'}
                    size="sm"
                    className="px-2.5"
                    role="radio"
                    aria-checked={drift.dir === d}
                    aria-label={d < 0 ? 'To the left' : 'To the right'}
                    onClick={() => set({ drift: makeDrift(d, drift.speed) })}
                  >
                    {d < 0 ? <ArrowLeft aria-hidden /> : <ArrowRight aria-hidden />}
                  </Button>
                ))}
              </div>
              <select className={cn(SELECT, 'h-9 w-auto')} value={drift.speed} onChange={(e) => set({ drift: makeDrift(drift.dir, e.target.value as (typeof DRIFT_SPEEDS)[number]['id']) })} aria-label="How fast">
                {DRIFT_SPEEDS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <span className="text-xs text-ink-3">Clouds, birds and fish look alive when they drift.</span>
          )}
        </div>
      </Group>

      <details className="rounded-xl border border-line p-3 text-sm">
        <summary className="cursor-pointer select-none font-medium text-ink-2">Exact position</summary>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <NumberBox label="From the left (%)" value={item.x} onCommit={(x) => onChange(updateItem(items, item.id, { x: clampX(x) }))} />
          <NumberBox label="From the top (%)" value={item.y} onCommit={(y) => onChange(updateItem(items, item.id, { y: clampY(y) }))} />
        </div>
        <p className="mt-2 text-xs text-ink-3">The point is where it touches the ground. Arrow keys move the selected thing too (Shift for bigger steps).</p>
      </details>
    </section>
  )
}

function Group({ label, aside, children }: { label: string; aside?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-ink">{label}</span>
        {aside ? <span className="text-xs tabular-nums text-ink-3">{aside}</span> : null}
      </div>
      {children}
    </div>
  )
}

function NumberBox({ label, value, onCommit }: { label: string; value: number; onCommit(v: number): void }) {
  const shown = Math.round(value * 10) / 10
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-2">
      {label}
      <input
        key={shown}
        type="number"
        min={0}
        max={100}
        step={0.5}
        defaultValue={shown}
        className="h-9 rounded-lg border border-line bg-paper px-2 text-sm tabular-nums text-ink focus-visible:focus-ring"
        onBlur={(e) => {
          const v = Number(e.target.value)
          if (Number.isFinite(v) && v !== shown) onCommit(v)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        }}
      />
    </label>
  )
}
